<template>
  <transition
    enter-class="opacity-0 scale-0"
    enter-active-class="transition ease-out duration-75"
    enter-to-class="opacity-100 scale-100"
    leave-class="opacity-100 scale-100"
    leave-active-class="transition ease-in duration-75"
    leave-to-class="opacity-0 scale-0"
  >
    <div
      v-if="show"
      v-tooltip.left="tooltipConfig"
      :class="badgeClasses"
    >
      <slot />
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type BadgeType = 'base' | 'inverse' | 'brand'
type BadgeSize = 'md' | 'sm'

interface Props {
  show?: boolean
  tooltip?: string
  type?: BadgeType
  // 'md' (default) is the 20px counter; 'sm' is an 8px dot indicator (no content)
  size?: BadgeSize
}

const props = withDefaults(defineProps<Props>(), {
  show: true,
  tooltip: '',
  type: 'base',
  size: 'md',
})

const tooltipConfig = computed(() => {
  if (!props.tooltip) return undefined
  return {
    value: props.tooltip,
    showDelay: 250,
  }
})

const typeClasses = {
  base: 'text-primary bg-surface shadow-sm',
  inverse: 'bg-surfaces-dark text-inverse',
  brand: 'bg-brand text-inverse',
}

// Size controls the dimensions and how far the badge is nudged off the corner.
// 'sm' is an 8px dot, so it sits closer to the corner and shows no text.
const sizeClasses = {
  md: 'size-5 text-xs -top-2 -right-2',
  sm: 'size-2 -top-1 -right-1',
}

const badgeClasses = computed(() => [
  'rounded-full select-none items-center justify-center flex absolute',
  sizeClasses[props.size],
  typeClasses[props.type],
])
</script>
